/**
 * Mail's Priority marker (Gmail's importance marker): filled amber when a conversation is in Priority, its tooltip
 * saying why (core logic/mail priorityReason); an outline on hover otherwise. Pressing it, the row menu, the toolbar,
 * the bulk bar and + / − mark it priority or not, for its sender's next mail too, with an Undo toast.
 */
import type { MailThread } from "@bundle/contracts";
import type { MailboxViewModel } from "@bundle/core";
import * as Mail from "@bundle/core/logic/mail";
import { IconButton, Stack, showMailUndo, type Translate } from "@bundle/ui";

const DAY = 86_400_000;

/** The sender the marks teach (the person on the thread), "" when it's only me. */
export function prioritySender(t: MailThread): string {
  const p = t.participants[0];
  return p === undefined ? "" : Mail.displayName(p);
}

/** "No reply in 3 days": whole days since my unanswered message (at least one). */
export function noReplyDays(t: MailThread, now: number): number {
  return Math.max(1, Math.floor((now - (t.noReplySince ?? now)) / DAY));
}

/** Why it's in Priority, as the marker's tooltip says it (undefined: it isn't). */
export function priorityWhy(tr: Translate, t: MailThread, now: number): string | undefined {
  const name = prioritySender(t);
  switch (t.priority) {
    case "waiting":
      return name === "" ? tr("mail.priority.waiting") : tr("mail.priority.waitingOn", { name });
    case "starred":
      return tr("mail.priority.starred");
    case "marked":
      return tr("mail.priority.marked");
    case "sender":
      return tr("mail.priority.sender", { name });
    case "reminder":
      return tr("mail.priority.reminder", { count: noReplyDays(t, now) });
    default:
      return undefined;
  }
}

/** Mark as priority / Not priority with the Undo toast: one conversation names its sender (taught too), several count. */
export function markPriority(tr: Translate, vm: MailboxViewModel, threads: MailThread[], on: boolean, selection: boolean): void {
  if (threads.length === 0) return;
  const undo = selection ? vm.markSelectedPriority(on) : vm.markPriority(threads[0]!.id, on);
  const name = threads.length === 1 ? prioritySender(threads[0]!) : "";
  const message =
    name !== ""
      ? tr(on ? "mail.undo.priority.sender" : "mail.undo.notPriority.sender", { name })
      : tr(on ? "mail.undo.priority" : "mail.undo.notPriority", { count: threads.length });
  showMailUndo(message, undo);
}

/**
 * The marker button: in Priority → filled, "Priority: why. Mark as not priority"; else an outline, in a row only while
 * it's hovered (`inRow`).
 */
export function PriorityMarker(p: { t: Translate; thread: MailThread; now: number; onToggle: (on: boolean) => void; inRow?: boolean; testID: string }) {
  const { t: tr, thread, now, onToggle, testID } = p;
  const size = p.inRow === true ? "sm" : "md";
  const why = priorityWhy(tr, thread, now);
  if (why !== undefined) {
    return <IconButton icon="priority" size={size} color="status.warning" accessibilityLabel={tr("mail.priority.marker", { reason: why })} onPress={() => onToggle(false)} testID={testID} />;
  }
  const mark = <IconButton icon="priority" size={size} color="text.tertiary" accessibilityLabel={tr("mail.action.markPriority")} onPress={() => onToggle(true)} testID={testID} />;
  return p.inRow === true ? <Stack revealOnHover>{mark}</Stack> : mark;
}
